import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  agentsApi, 
  AgentCreateRequest, 
  AgentUpdateRequest, 
  AgentsListParams
} from '@/services/agents'
import { toast } from 'sonner'

// Query keys
const agentsKeys = {
  all: ['agents'] as const,
  lists: () => [...agentsKeys.all, 'list'] as const,
  list: (params: AgentsListParams) => [...agentsKeys.lists(), params] as const,
  details: () => [...agentsKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentsKeys.details(), id] as const,
}

// Queries
export function useAgents(params?: AgentsListParams) {
  return useQuery({
    queryKey: agentsKeys.list(params || {}),
    queryFn: () => agentsApi.list(params),
    select: (response) => ({
      agents: response.data.data,
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: response.data.totalPages
    }),
  })
}

export function useAgent(id: string) {
  return useQuery({
    queryKey: agentsKeys.detail(id),
    queryFn: () => agentsApi.get(id),
    select: (response) => response.data.data, // Extract data from {success, data}
    enabled: !!id,
  })
}

// Mutations
export function useCreateAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AgentCreateRequest) => agentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentsKeys.lists() })
      toast.success('Agent created successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create agent')
    },
  })
}

export function useUpdateAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AgentUpdateRequest }) => 
      agentsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: agentsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentsKeys.detail(id) })
      toast.success('Agent updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update agent')
    },
  })
}

export function useDeleteAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentsKeys.lists() })
      toast.success('Agent deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete agent')
    },
  })
}

export function useApproveAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agentsApi.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: agentsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agentsKeys.detail(id) })
      toast.success('Agent approved successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to approve agent')
    },
  })
}
